import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, asList, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = (fields) => Object.fromEntries(fields.map((f) => [f.k, f.options ? f.options[0] : f.type === "number" ? 0 : f.list ? [] : ""]));

const FieldInput = ({ f, value, onChange }) => {
  const props = { "data-testid": `admin-field-${f.k}`, className: "field", value: value ?? "", onChange: (e) => onChange(e.target.value), required: f.required };
  if (f.options) return <select {...props}>{f.options.map((o) => <option key={o}>{o}</option>)}</select>;
  if (f.area) return <textarea rows={3} {...props} className="field resize-none" />;
  if (f.list) return <input {...props} value={Array.isArray(value) ? value.join(", ") : value ?? ""} />;
  return <input type={f.type || "text"} {...props} />;
};

export const EntityManager = ({ path, fields, name }) => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(path).then((r) => setRows(asList(r))).catch(() => setRows([]));
  useEffect(() => { load(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    const body = { ...edit };
    fields.filter((f) => f.type === "number").forEach((f) => { body[f.k] = Number(body[f.k]) || 0; });
    fields.filter((f) => f.list).forEach((f) => {
      const v = body[f.k];
      body[f.k] = Array.isArray(v) ? v : String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
    });
    try {
      if (edit.id) await api.put(`${path}/${edit.id}`, body); else await api.post(path, body);
      toast.success("Saved"); setEdit(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm(`Delete this ${name}?`)) return;
    try { await api.delete(`${path}/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mt-6">
      <button data-testid={`admin-add-${name}`} onClick={() => setEdit(blank(fields))}
        className="inline-flex items-center gap-2 clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[var(--bone)]">
        <Plus className="w-4 h-4" /> Add {name}
      </button>
      <div className="mt-6 border-t border-[var(--line)]">
        {rows.length === 0 && <p className="py-8 text-sm text-[var(--muted)]">Nothing here yet.</p>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`admin-row-${r.id}`} className="grid grid-cols-12 gap-3 items-center py-4 border-b border-[var(--line)]">
            {r.image !== undefined && <img src={r.image || "/img/hoodie-front.jpg"} alt="" className="col-span-2 sm:col-span-1 w-12 h-12 object-cover" />}
            <div className={r.image !== undefined ? "col-span-6 sm:col-span-8" : "col-span-8 sm:col-span-9"}>
              <p className="font-display text-xl font-bold uppercase">{r[fields[0].k]}</p>
              <p className="font-mono text-[11px] text-[var(--muted)] uppercase tracking-[0.15em]">
                {fields.slice(1, 5).map((f) => r[f.k]).filter((v) => v !== "" && v !== undefined).join(" · ")}
              </p>
            </div>
            <div className="col-span-4 sm:col-span-3 flex justify-end gap-2">
              <button data-testid={`admin-edit-${r.id}`} onClick={() => setEdit(r)} className="p-2.5 border border-[var(--line)] hover:border-[var(--bone)]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`admin-delete-${r.id}`} onClick={() => remove(r.id)} className="p-2.5 border border-[var(--line)] hover:border-red-400 hover:text-red-400" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="admin-edit-dialog" className="bg-[var(--ink-2)] border-[var(--line)] rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">{edit?.id ? "Edit" : "New"} {name}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="space-y-4">
              {fields.map((f) => (
                <label key={f.k} className="block"><span className="field-label">{f.label}</span>
                  <FieldInput f={f} value={edit[f.k]} onChange={(v) => setEdit({ ...edit, [f.k]: v })} /></label>
              ))}
              <button data-testid="admin-save-button" className="w-full clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-[var(--bone)]">Save</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
